import { useCallback, useEffect, useRef } from 'react';
import { findAnswerForQuestion } from './rubric-utils';
import './styles/index.scss';
export interface UseRubricAutofillOptions {
  /** The entity ID (code ID or sd workitem ID) */
  entityId: string;
  /** The key prefix for tracking (e.g., "case-123" or just "456") */
  entityKey: string;
  /** Current attributes on the entity */
  attributes: CQA.Attribute[];
  /** Map of question IDs to answer value IDs that should be autofilled */
  autofilledAnswers: Map<number, number[]>;
  /** Callback to apply an autofill (same signature as handleAnswerChange) */
  onAutofill: (questionId: number, answerValueId: number) => void;
}

export interface UseRubricAutofillResult {
  /** Call when user changes an answer to track pending state */
  trackAnswerChange: (questionId: number, answerValueId: number) => void;
  /** Call to clear tracking for downstream questions being cleared */
  clearDownstreamTracking: (downstreamQuestionIds: number[]) => void;
}

/**
 * Hook that manages autofill logic for rubric questions.
 *
 * Handles the complex timing of:
 * 1. Preventing autofill when state is stale (race condition with API)
 * 2. Applying autofill when triggers fire
 * 3. Tracking which questions have been autofilled to prevent loops
 * 4. Resetting tracking when the entity changes
 * 5. Clearing downstream tracking when upstream answers change
 *
 * @example
 * const { trackAnswerChange, clearDownstreamTracking } = useRubricAutofill({
 *   entityId: code.id,
 *   entityKey: `${code.id}`,
 *   attributes: code.attributes || [],
 *   autofilledAnswers,
 *   onAutofill: handleAnswerChange
 * });
 *
 * // In your handleAnswerChange:
 * trackAnswerChange(questionId, answerValueId);
 * clearDownstreamTracking(downstreamIds);
 * await handleAnswerChangeWithCleanup({...});
 */
export function useRubricAutofill({
  entityId,
  entityKey,
  attributes,
  autofilledAnswers,
  onAutofill
}: UseRubricAutofillOptions): UseRubricAutofillResult {
  // Track which questions have had autofill applied to prevent loops
  const appliedAutofillsRef = useRef<Set<string>>(new Set());

  // Track pending answer change to verify state is fresh before autofilling
  const pendingAnswerRef = useRef<{ questionId: number; answerValueId: number } | null>(null);

  // When downstream answers are cleared, block autofills until fresh evaluation data arrives.
  // Without this, stale autofilledAnswers can re-apply a cleared autofill before the new
  // evaluation (which no longer includes the autofill) has returned.
  const awaitingFreshEvalRef = useRef(false);

  // Questions that were cleared downstream (user went back and changed an upstream answer).
  // These require a new explicit manual answer — autofill is suppressed for them so the
  // wizard shows them as active questions rather than silently re-filling them with the
  // same value the evaluation happens to suggest.
  const requireManualAnswerRef = useRef<Set<string>>(new Set());

  // Reset the flag when evaluation data refreshes (autofilledAnswers reference changes).
  // This effect MUST be defined before the autofill effect so it runs first in the same
  // render cycle, allowing the autofill effect to proceed with fresh data.
  //
  // Also lift the manual-re-answer requirement for any question the *fresh* evaluation
  // confirms is an autofill question. This is the authoritative signal: a cleared
  // downstream question that the new evaluation still autofills should be re-autofilled
  // (with its new value), while one absent from the map stays cleared to be re-asked.
  useEffect(() => {
    awaitingFreshEvalRef.current = false;
    autofilledAnswers.forEach((_answerValueIds, questionId) => {
      requireManualAnswerRef.current.delete(`${entityKey}-${questionId}`);
    });
  }, [autofilledAnswers, entityKey]);

  // Apply auto-filled answers when triggers fire
  useEffect(() => {
    // Skip autofills while waiting for fresh evaluation data after a downstream clear.
    // The stale autofilledAnswers map may still reference cleared questions.
    if (awaitingFreshEvalRef.current) return;

    // If there's a pending answer change, check if it's reflected in state
    if (pendingAnswerRef.current) {
      const { questionId, answerValueId } = pendingAnswerRef.current;
      const currentAnswer = findAnswerForQuestion(attributes, questionId);
      // If the pending answer is NOT yet in state, skip (data is stale)
      if (!currentAnswer || currentAnswer.answerValueId !== answerValueId) {
        return;
      }
      // Answer IS reflected in state, clear pending and proceed
      pendingAnswerRef.current = null;
    }

    if (autofilledAnswers.size === 0) return;

    autofilledAnswers.forEach((answerValueIds, questionId) => {
      const autofillKey = `${entityKey}-${questionId}`;
      // Skip if we've already applied autofill for this question
      if (appliedAutofillsRef.current.has(autofillKey)) return;
      // Skip if the question was cleared downstream — the user must re-answer it manually
      if (requireManualAnswerRef.current.has(autofillKey)) return;

      // Only apply if the question doesn't already have an answer
      const existingAnswer = findAnswerForQuestion(attributes, questionId);
      if (!existingAnswer && answerValueIds.length > 0) {
        appliedAutofillsRef.current.add(autofillKey);
        // Apply the first answer value (for single-select questions)
        onAutofill(questionId, answerValueIds[0]);
      }
    });
  }, [autofilledAnswers, attributes, entityKey, onAutofill]);

  // Reset autofill tracking when the entity changes
  useEffect(() => {
    appliedAutofillsRef.current.clear();
    requireManualAnswerRef.current.clear();
  }, [entityId]);

  // Track an answer change to verify state is fresh before autofilling.
  // Also clears any manual-re-answer requirement for this question.
  const trackAnswerChange = useCallback((questionId: number, answerValueId: number) => {
    pendingAnswerRef.current = { questionId, answerValueId };
    requireManualAnswerRef.current.delete(`${entityKey}-${questionId}`);
  }, [entityKey]);

  // Clear tracking for downstream questions being cleared. Provisionally mark every
  // cleared question as requiring a manual re-answer; the fresh-evaluation effect above
  // lifts that requirement for any question the new evaluation still autofills. This
  // prevents the stale autofilledAnswers map from silently re-filling a cleared question
  // with its old value before the fresh evaluation lands.
  const clearDownstreamTracking = useCallback((downstreamQuestionIds: number[]) => {
    downstreamQuestionIds.forEach(id => {
      const key = `${entityKey}-${id}`;
      appliedAutofillsRef.current.delete(key);
      requireManualAnswerRef.current.add(key);
    });
    if (downstreamQuestionIds.length > 0) {
      awaitingFreshEvalRef.current = true;
    }
  }, [entityKey]);

  return { trackAnswerChange, clearDownstreamTracking };
}

export default useRubricAutofill;
